import React from 'react';
import { SanitizedText } from '@/ui/SanitizedDom';
import { Flex, Icon } from '@chakra-ui/react';
import {
  getSelectionRatingStyle,
  ratingOptions,
} from '@/constants/scorecard-configs';
import { KText } from 'ui-library/components';
import { QuestionType, ScorecardType } from '../types/interview';
import { AttachmentType } from '../types/attachment.interface';
import { Star01 } from '@kula-ai/icons/solid/Star01';
import { Star01 as Star01Outline } from '@kula-ai/icons/outline/Star01';
import { FileAttachmentPreviewReadonly } from '@/components/formBuilder/formFields/FormEditor/FileAttachmentPreviewReadonly';

const QuestionBox = ({
  title,
  content,
  description,
  type,
  options,
  attachments,
}: {
  title: string;
  description: string;
  content: string | number | string[];
  type: QuestionType;
  options?: string[];
  attachments?: AttachmentType[];
}) => {
  const wrapperStyles = {
    p: '2px 8px',
    borderRadius: 'xs',
    width: 'fit-content',
    border: '1px solid',
    borderColor: 'secondary-700',
  };

  const selectedStyle = {
    bg: 'secondary-700',
    color: 'base-white',
    width: 'fit-content',
    border: 'none',
  };

  const renderStars = () => {
    const stars = Array.from({ length: 5 }, () => 5);
    const value = content as number;

    return (
      <Flex gap="12px" flexWrap="wrap">
        {stars?.map((_, index) => (
          <Icon
            as={index + 1 <= value ? Star01 : Star01Outline}
            width="24px"
            height="24px"
            key={index}
          />
        ))}
      </Flex>
    );
  };

  const renderRating = () => {
    const rating = Array.from({ length: 10 }, () => 10);

    return (
      <Flex gap="12px" flexWrap="wrap">
        {rating?.map((_, index) => (
          <KText
            variant="paragraph"
            key={index}
            {...wrapperStyles}
            {...(content?.toString() === (index + 1).toString() &&
              selectedStyle)}
            width="unset"
            minWidth="40px"
            textAlign="center"
          >
            {index + 1}
          </KText>
        ))}
      </Flex>
    );
  };

  const renderHtml = () => {
    return (
      <SanitizedText
        html={content}
        variant="heading_4"
        className="editor-style"
      />
    );
  };

  const renderOptions = () => {
    return (
      <Flex gap="12px" flexWrap="wrap">
        {options?.map((item, index) => {
          const isSelected =
            type === 'checkbox'
              ? (content as string[])
                  ?.map((item) => item?.toString()?.toLowerCase())
                  ?.includes(item?.toString()?.toLowerCase())
              : content?.toString()?.toLowerCase() ===
                item?.toString()?.toLowerCase();

          return (
            <KText
              variant="paragraph"
              key={index}
              {...wrapperStyles}
              {...(isSelected && selectedStyle)}
            >
              {item}
            </KText>
          );
        })}
      </Flex>
    );
  };

  const contentMap = {
    star: renderStars,
    one_to_ten: renderRating,
    open_question: renderHtml,
    multiple_choice: renderOptions,
    checkbox: renderOptions,
    yes_no: renderOptions,
  };

  return (
    <Flex
      flexDir="column"
      py="20px"
      borderBottom="1px solid"
      borderColor="border-color"
      gap="10px"
    >
      <KText variant="heading_4">{title}</KText>
      {description && <KText variant="paragraph">{description}</KText>}

      {!content && <KText variant="paragraph">-</KText>}
      {content && contentMap[type]()}
      {attachments && attachments.length > 0 && (
        <Flex flexDir="column" padding="20px" gap="10px" paddingLeft={'0px'}>
          <Flex gap="12px" flexWrap="wrap">
            {attachments.map((attachment, index) => (
              <FileAttachmentPreviewReadonly key={index} file={attachment} />
            ))}
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};

export const InterviewScorecardReadOnly = ({
  scoreCard,
}: {
  scoreCard: ScorecardType;
}) => {
  const {
    questions = [],
    feedback,
    feedback_description,
    rating_description,
    feedback_attachments,
    rating,
  } = scoreCard || {};
  const ratingConfig = ratingOptions?.find(
    (item) => item?.value === scoreCard?.rating
  );

  const scorecardStyles = getSelectionRatingStyle(ratingConfig);

  if (!scoreCard?.id) {
    return <KText size="sm-medium">No scorecard data found</KText>;
  }

  return (
    <Flex flexDir="column">
      {questions.map((item, idx) => (
        <QuestionBox
          key={idx}
          title={item.question}
          content={item.answer}
          description={item.description}
          type={item.type}
          options={item.type === 'yes_no' ? ['Yes', 'No'] : item.options}
          attachments={item.attachments || []}
        />
      ))}
      <QuestionBox
        title="Feedback"
        content={feedback}
        description={feedback_description}
        type="open_question"
        attachments={feedback_attachments || []}
      />
      <Flex
        flexDir="column"
        py="20px"
        border="1px solid border-color"
        borderRadius="sm"
        gap="10px"
      >
        <KText size="sm-medium" color="secondary-900">
          Overall evaluation
        </KText>
        <KText size="sm-regular" color="secondary-700">
          {rating_description}
        </KText>
        {rating ? (
          <Flex
            gap="6px"
            alignItems="center"
            borderRadius="4px"
            bg={ratingConfig?.bgColor}
            p="0.125em 0.5em"
            w="fit-content"
          >
            <Icon
              as={ratingConfig?.icon}
              w="24px"
              h="24px"
              color={ratingConfig?.selectedColor}
              {...scorecardStyles}
            />
            <KText size="sm-medium" color={ratingConfig?.selectedColor}>
              {ratingConfig?.label}
            </KText>
          </Flex>
        ) : (
          <KText size="sm-medium" color="secondary-700">
            -
          </KText>
        )}
      </Flex>
    </Flex>
  );
};
